import React, { useCallback } from 'react';
import { Space, message, Breadcrumb, Table } from 'antd';
import { 
  CardLayout, 
  TableWrapper,
  CompanyCashEditModal } from 'components/molecules';
import { addNewSearch } from 'utils/utils';
import { useLocation, useNavigate } from 'react-router';
import { usePopupManager } from 'react-popup-manager';
import { ColumnsType } from 'antd/lib/table/interface';
import qs from 'query-string';
import { TotalNumMenu } from 'components/atoms';
import { useTableInfo, useCompanyPoint } from 'hooks';
import { INIT_PAGESIZE } from 'commons/constants';
import SearchBox from '../CachSearchBox'
import axios from 'axios';
import { endpoints } from 'services';
import { SERVER_URL } from 'commons/constants';
import moment from 'moment';

export interface  CompanyCashProps {}

const  CompanyPoint = ({}:  CompanyCashProps): React.ReactElement => {
  /* ======================================================
   * hooks
   * ====================================================== */
  const navigate = useNavigate();
  const location = useLocation();  
  const { cashList, searchResult, companyCashInsert } = useCompanyPoint();  

  /* ======================================================
   * variables
   * ====================================================== */
  const { open } = usePopupManager();

  const searchParams = qs.parse(location.search);  
  const columns: ColumnsType<CompanyCashListDto> = [
    {
      title: '거래번호',
      dataIndex: 'id',
      key: 'id',
      width: '100px',
      align: 'center',
    },  
    {
      title: '등록구분',
      dataIndex: 'cash_gubun',
      key: 'cash_gubun',
      width: '100px',
      align: 'center',   
      render: (cash_gubun, record) => {
        return (
          cash_gubun==='IN'?'입금':cash_gubun==='OUT'?'차감':'매출'
        )
      } 
    },  
    {
      title: '포인트',
      key: 'cash',
      dataIndex: 'cash',
      width: '120px',
      align: 'right',
      render: (cash, record) => {
        const newcash = record.cash_gubun==='OUT'?cash*-1:cash;
        return (
          newcash.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        )        
      } 
    },
    {
      title: '발생일시',
      key: 'created_at',
      dataIndex: 'created_at',
      width: '200px',
      align: 'center',
    },     
    {
      title: '잔여포인트',
      key: 'remain_cash',
      dataIndex: 'remain_cash',
      width: '120px',
      align: 'right',
      render: (remain_cash, record) => {
        return (
          remain_cash.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        )
      } 
    },    
    {
     title: '등록자',
     key: 'who_create',
     dataIndex: 'who_create',
     width: '120px',
     align: 'center',
   },   
    {
      title: '메모',
      key: 'memo',
      dataIndex: 'memo',
      width: 'auto',
      align: 'left',
      ellipsis: true, 
    },
  ];

  /* ======================================================
   * events
   * ====================================================== */   
  const onClickRegister = () => {
    open(CompanyCashEditModal, {
      id: 0,
      cash_type: "POINT",
      totalcash: searchResult.cash,
      onClose: (isOk, cash_gubun, cash, memo) => {
        if (isOk) {   
          companyCashInsert({
            id: searchResult.company_srl,
            cash_gubun: cash_gubun,
            cash: cash,
            memo: memo,
          })             
        };
      },
    });     
  }

  const onExcelDownload = () => {
    axios({
      url: SERVER_URL + endpoints.company.point.downloadexcel,
      method: 'post',
      responseType: 'arraybuffer',
      params: {...searchParams, company: searchResult.company_srl},
    })
    .then((response) => {
      const fileName = "포인트관리_" + moment().format('YYYYMMDD_HHmmss')+".xlsx";
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fileName}`);
      link.style.cssText = 'display:none';
      document.body.appendChild(link);
      link.click();

    })
    .catch(() => {
      message.error('파일 다운로드 중 에러가 발생했습니다.', 1);
    });     
  }    

  const onChangePageInfo = (pageNo: number, pageSize: number) => {
    navigate({
      ...location,
      search: addNewSearch(location.search, { pageNo, pageSize }),
    });
  };    

 const onRowClick = useCallback((record, index) => {
  return {
    onClick: () => {
      open(CompanyCashEditModal, {
        id: record.id,
        cash_type: "POINT",
        cash: record.cash,
        cash_gubun: record.cash_gubun,
        memo: record.memo,        
      }); 
    },
  };
 }, []);
  /* ======================================================
   * hooks
   * ====================================================== */
  const { pagination, onTableChange } = useTableInfo({
    pageInfo: {
      pageNo: Number(searchParams.pageNo) || 1,
      pageSize: Number(searchParams.pageSize) || INIT_PAGESIZE,
    },
    datas: cashList,
    totalCount: searchResult.totalCount,
    onChangePageInfo,
  });

  /* ======================================================
   * render items
   * ====================================================== */
  return (
    <CardLayout title={(
      <Breadcrumb>
        <Breadcrumb.Item>Home</Breadcrumb.Item>
        <Breadcrumb.Item>고객사</Breadcrumb.Item>
        <Breadcrumb.Item>{searchResult.company}</Breadcrumb.Item>
        <Breadcrumb.Item>포인트관리</Breadcrumb.Item>
    </Breadcrumb>      
    )}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <SearchBox />
          <TotalNumMenu 
            totalNum={searchResult.totalCount} 
            text={'총 '+searchResult.totalCount+' 개의 항목이 있습니다. 잔여포인트 : '+searchResult.cash.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            useRegister 
            useExcelDownload 
            onUseRegister={onClickRegister} 
            onDownloadExcel={onExcelDownload}
          />
          <TableWrapper isSearch={!!searchParams.search || !!searchParams.lecturer_type || !!searchParams.lecturer_category}>
            <Table
              tableLayout="fixed"
              dataSource={cashList}
              columns={columns}
              pagination={pagination}
              onChange={onTableChange}
              onRow={onRowClick}
              style={{ width: '100%', overflowX: 'auto', minWidth: 600 }}
              className="columnCenterFixTable clickable"
              showSorterTooltip={false}
              rowKey={'id'}
            />
          </TableWrapper>            
        </Space>                
    </CardLayout>
  )
};

export default  CompanyPoint;    